import React, { useState, useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'
import FullCalendar from '@fullcalendar/react'
import { BASE_OPTION_REFINERS } from '@fullcalendar/core'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import altViewPlugin from '../reservations-alt-view'
import CalendarHeader from './calendar-header'
import Pikaday from 'pikaday'
import Cable from 'actioncable'
import DailyGames from '../daily-games-modal'
import GameModal from '../../containers/game-modal'
import OpenGameModal from '../../containers/open-game-modal'
import FacilityOpenGameModal from '../../containers/facility-open-game-modal'
import NewGameForm from '../new-game-form'
import OpenGameForm from '../open-game-form'
import track from '../../utils/mixpanel-event'
import Signature from '../../components/signature'

const Calendar = props => {
  BASE_OPTION_REFINERS.schedulerLicenseKey = String

  const [viewTitle, setViewTitle] = useState()
  const [picker, setPicker] = useState()
  const [showDate, setShowDate] = useState(null)
  const [showGame, setShowGame] = useState(null)
  const [showOpenGame, setShowOpenGame] = useState(null)
  const [showFacilityOpenGame, setShowFacilityOpenGame] = useState(null)
  const [showGameForm, setShowGameForm] = useState(null)
  const [showOpenGameForm, setShowOpenGameForm] = useState(null)

  const calendarRef = useRef(null)

  const customButtons = () => {
    let buttons = {
      datePickerTitle: {
        text: viewTitle,
        click: () => {
          picker && picker.setMoment(moment(calendarRef.current.getApi().getDate()))
        }
      }
    }

    if (props.compoundFieldsNames) {
      buttons.compoundFields = { 
        text: props.compoundFieldsNames,
        click: (e) => {
          props.fieldMode == 'simple' ? e.target.classList.add('disabled') : e.target.classList.remove('disabled')
          props.onFieldModeChange()
        }
      }
    }

    return buttons
  }

  const onVisibleFields = (data) => {
    const fields = calendarRef.current.getApi().currentDataManager.getCurrentData().resourceSource.meta
    return typeof fields.find(e => e.id == data.field_id) != 'undefined'
  }

  const onVisibleDates = (data) => {
    const cv = calendarRef.current.getApi().view
    const visibleDate = cv.type == 'Alt' ? [...new Array(5)].map((e, i) => moment(cv.activeStart).subtract(7 * i, 'd')).some(e => e.isSame(data.date)) : false

    return visibleDate || moment(data.date).isSame(cv.activeStart) || moment(data.date).isBetween(cv.activeStart, cv.activeEnd)
  }

  const eventOnCalendar = (data) => {
    if (calendarRef.current.getApi().view.type == 'Alt') return true

    return calendarRef.current.props.events.some(el => el.id == data.event.id)
  }

  useEffect(() => {
    if (!props.sdAdminLevel) track('Page View', {page: 'Reservations'})
    const cable = Cable.createConsumer(props.socketUrl)
    const locationsChannel = cable.subscriptions.create({ channel: 'LocationsChannel' }, 
    {
      received: (data) => {
        if (onVisibleFields(data) && (onVisibleDates(data) || eventOnCalendar(data))) {
          props.onLocationsUpdate(data)
        }
      }
    })
    const openGameChannel = cable.subscriptions.create({ channel: 'OpenGameChannel' },
    {
      received: (data) => {
        props.onEventUpdate(data)
      }
    })
    return () => {
      cable.subscriptions.remove(locationsChannel)
      cable.subscriptions.remove(openGameChannel)
    }
  }, [])

  useEffect(() => {
    const datePickerButton = document.querySelector('.fc-datePickerTitle-button')

    if (datePickerButton) {
      setPicker(
        new Pikaday({
          field: datePickerButton,
          onSelect: (date) => {
            const calendarDate = moment(calendarRef.current.getApi().getDate()).format()
            const pickerDate   = moment(date).format()

            if (calendarDate != pickerDate)
              calendarRef.current.getApi().gotoDate(moment(date).format('YYYY-MM-DD'))
          }
        })
      )
    }
  }, [])

  const clickDebounce = (() => {
    let isDebounce = false
    const reset = () => isDebounce = false
    return () => {
      if (isDebounce) return true
      isDebounce = true
      setTimeout(reset, 500)
      return false
    }
  })()
  
  const handleDateChange = dateInfo => {
    if (dateInfo.view.type == 'resourceTimeGridDay') {
      setViewTitle(moment(dateInfo.start).format('dddd, MMMM D, YYYY'))
    } else {
      setViewTitle(dateInfo.view.title)
    }

    props.onDateChange({dateInfo: dateInfo, calendarRef: calendarRef})
  }

  const handleDateClicked = data => {
    const date = moment(data.dateStr)
    const params = { date: moment(data.dateStr).subtract({ hours: 6 }).format('MM/DD/YYYY'), field: data.resource && data.resource._resource.id }
    if (data.view.type != 'dayGridMonth') {
      const minutes = props.timeFractions.slice().reverse().find(e => e <= date.minutes())
      if (minutes != undefined) {
        params.default_time = date.format(`YYYY-MM-DD HH:${String(minutes).padStart(2, '0')}:SS`)
      } else {
        params.default_time = date.subtract(1, 'hour').format(`YYYY-MM-DD HH:${String(props.timeFractions.slice(-1)).padStart(2, '0')}:SS`)
      }
    }
    props.sdAdminLevel ? setShowOpenGameForm({type: 'new', params: params}) : setShowGameForm(params)
  }

  const handleEventClicked = data => {
    if (data.event._def.extendedProps.category_name == 'Google') return

    if (document.getElementsByClassName('fc-more-popover').length)
      document.getElementsByClassName('fc-more-popover')[0].style.display = 'none'

    if (clickDebounce()) return

    const gameId = parseInt(data.event._def.publicId)
    const date = data.event._def.extendedProps.day
    const isOpenGame = data.event._def.extendedProps.isOpenGame

    if (Object.is(NaN, gameId)) return setShowDate(date)

    if (isOpenGame) {
      props.sdAdminLevel ? setShowOpenGame(gameId) : setShowFacilityOpenGame(gameId)
    } else {
      setShowGame(parseInt(gameId))
    }   
  }

  const renderIcons = (details) => {
    const icons = details.icons
    const showNotes = !details.isOpenGame || (details.isOpenGame && props.sdAdminLevel)
    const showIcons = !details.isOpenGame || props.sdAdminLevel

    return <div className='event-icons'>
      { icons.with_notes && showNotes && <i className='with-notes'></i> }
      { icons.confirmed && showIcons && <i className='fa fa-solid fa-check-circle'></i> }
      { icons.confirmed_wf && showIcons && <i className='fa fa-solid fa-check-circle blue'></i> }
      { icons.pre_booked && showIcons && <i className='fa fa-solid fa-check-circle purple'></i> }
    </div>
  }

  const renderEvent = eventData => {
    const details = eventData.event._def.extendedProps

    if (!_.isEmpty(details.icons)) {      
      eventData.el.insertAdjacentHTML('beforeend', ReactDOMServer.renderToString(renderIcons(details)))
    }
    if (details.isOpenGame) {
      eventData.el.insertAdjacentHTML('beforeend', `<div id='game-id-${eventData.event._def.publicId}' class='calendar-tile-player-count'>${details.total}</div>`)
    }
    if (details.description && eventData.view.type == 'resourceTimeGridDay') {
      eventData.el.insertAdjacentHTML('beforeend', `<div class='unpaid_amount'>$${details.description}</div>`)
    }
    if (eventData.view.type != 'dayGridMonth') {
      eventData.el.insertAdjacentHTML('beforeend', "<div class='fc-bg'></div>")
    }
  }

  const duplicateGame = (id) => {
    setShowOpenGame(null)
    setShowOpenGameForm({type: 'duplicate', id: id})
  }

  const onShowOpenGame = (gameId) => {
    props.sdAdminLevel ? setShowOpenGame(gameId) : setShowFacilityOpenGame(gameId)
  }

  const updateOpenGame = (data) => {
    setShowOpenGameForm(false)
    props.onRefreshOpenGame(data)
  }

  return (
    <div className='reservation-calendar__panel'>
      { showDate && 
        <DailyGames
          date={showDate}
          onClose={() => setShowDate(null)}
          onShowGame={(id) => setShowGame(id)}
          onShowOpenGame={(id) => onShowOpenGame(id)}
        /> 
      }
      { showGame &&
        <GameModal
          gameId={showGame}
          socketUrl={props.socketUrl}
          onClose={() => setShowGame(null)}
        /> 
      }
      { showOpenGame &&
        <OpenGameModal
          activeTab='game-details'
          gameId={showOpenGame}
          socketUrl={props.socketUrl}
          onClose={() => setShowOpenGame(null)}
          onDuplicate={duplicateGame}
          onEdit={(data) => setShowOpenGameForm(data) }
          onCancel={() => setShowOpenGame(null)}
        /> 
      }
      { showFacilityOpenGame &&
        <FacilityOpenGameModal
          gameId={showFacilityOpenGame}
          socketUrl={props.socketUrl}
          onClose={() => setShowFacilityOpenGame(null)}
        /> 
      }
      { showGameForm &&
        <NewGameForm params={showGameForm} onClose={() => setShowGameForm(null)} /> 
      }
      { showOpenGameForm &&
        <OpenGameForm
          type={showOpenGameForm.type} 
          id={showOpenGameForm.id} 
          recurringId={showOpenGameForm.recurringId}
          params={showOpenGameForm.params}
          onClose={() => setShowOpenGameForm(false)}
          onUpdate={updateOpenGame}
        /> 
      }
      <div className='panel-body'>
        <CalendarHeader 
          summary={props.summary}
          canCreate={props.canCreate}
          sdAdminLevel={props.sdAdminLevel}
          onNewGame={() => setShowGameForm({})}
          onNewOpenGame={() => setShowOpenGameForm({type: 'new'})}
        />
      </div>
      { props.isLoading && <div className="animated-spinner"></div> }
      { props.signature && 
        <Signature
          {...props.signature}
          onComplete={props.completeSignature}
          onClose={props.stopSignature}
          onSendReceipt={props.onSendReceipt}
        />
      }
      <div className={`panel-body ${props.isLoading ? ' isLoading' : ''}`}>
        <FullCalendar
          schedulerLicenseKey='CC-Attribution-NonCommercial-NoDerivatives'
          allDaySlot={false}
          nowIndicator={true}
          plugins={ [timeGridPlugin, dayGridPlugin, interactionPlugin, altViewPlugin, resourceTimeGridPlugin] }
          initialView={props.viewType || 'resourceTimeGridDay'}
          headerToolbar={{
            start: 'prev,next today' + (customButtons().compoundFields ? ' compoundFields' : ''),
            center: 'datePickerTitle',
            end: 'Alt resourceTimeGridDay,timeGridFourDay,timeGridWeek,dayGridMonth'
          }}
          customButtons={customButtons()}
          views={{
            timeGridFourDay: {
              type: 'timeGrid',
              duration: { days: 4 },
              buttonText: '4 day'
            },
            resourceTimeGridDay: {
              titleFormat: {
                month: 'long',
                year: 'numeric',
                day: 'numeric',
                weekday: 'long'
              }
            },
            Alt: {
              titleFormat: {
                month: 'long',
                year: 'numeric',
                day: 'numeric',
                weekday: 'long'
              }
            }
          }}
          nextDayThreshold='09:00:00'
          timeZone='local'
          dayMaxEvents={4}
          initialDate={props.initialDate}
          eventDidMount={(eventData) => renderEvent(eventData)}
          resources={props.fieldsData[props.fieldMode]}
          resourceOrder='field_number'
          contentHeight={window.innerHeight - 280}
          slotMinTime={props.minTime ? props.minTime : '00:00:00'}
          slotMaxTime={props.maxTime ? props.maxTime : '24:00:00'}
          slotDuration='00:15:00'
          slotLabelInterval='00:30:00'
          eventDisplay='block'
          ref={calendarRef}
          datesSet={(dateInfo) => {handleDateChange(dateInfo)}}
          eventClick={(data) => handleEventClicked(data)}
          dateClick={(date) => handleDateClicked(date)}
          events={props.events}
        />
      </div>
    </div>
  )
}

export default Calendar
